// /admin/tenants/new — operator onboarding wizard. Four steps:
// Datos → Primer agente → Conexión Meta → Dueño. Composes the
// design-system Wizard chrome + WizardOptionCards. Creates via the createTenant()
// seam, wired to POST /admin/tenants. One plan for everyone right now, so the
// subscription step is gone and every client is created on 'profesional'.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { createTenant, type NewTenantInput, type TenantVertical } from './tenants-data.js'
import {
  Banner,
  FormField,
  Input,
  PageHeader,
  Textarea,
  toast,
  Wizard,
  WizardOptionCards,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'

const STEPS = [
  { id: 'data', label: 'Datos' },
  { id: 'bot', label: 'Perfil inicial del agente' },
  { id: 'meta', label: 'Conexión Meta' },
  { id: 'owner', label: 'Dueño' },
]

interface FormState {
  name: string
  vertical: TenantVertical | ''
  botName: string
  welcome: string
  connectMeta: 'now' | 'later'
  ownerName: string
  ownerEmail: string
}

const EMAIL_RE = /^\S+@\S+\.\S+$/

function StepHeading({ title, sub }: { title: string; sub: string }): JSX.Element {
  return (
    <div className="mb-4">
      <h3 className="text-[15px] font-bold text-text">{title}</h3>
      <p className="mt-0.5 text-[12px] text-text-2">{sub}</p>
    </div>
  )
}

export function NewTenantPage(): JSX.Element {
  const navigate = useNavigate()
  const [stepId, setStepId] = useState('data')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [form, setForm] = useState<FormState>({
    name: '',
    vertical: '',
    botName: '',
    welcome: '',
    connectMeta: 'now',
    ownerName: '',
    ownerEmail: '',
  })

  function set<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((f) => ({ ...f, [key]: value }))
  }

  const index = STEPS.findIndex((s) => s.id === stepId)
  const isLast = index === STEPS.length - 1

  const stepValid: Record<string, boolean> = {
    data: form.name.trim().length > 0 && form.vertical !== '',
    bot: form.botName.trim().length > 0,
    meta: true,
    owner: form.ownerName.trim().length > 0 && EMAIL_RE.test(form.ownerEmail),
  }

  async function onNext(): Promise<void> {
    if (!isLast) {
      const next = STEPS[index + 1]
      if (next) setStepId(next.id)
      return
    }
    if (form.vertical === '') return
    setSubmitting(true)
    setSubmitError('')
    try {
      const payload: NewTenantInput = {
        name: form.name.trim(),
        vertical: form.vertical,
        // One plan for everyone right now, so every client is created on the full
        // 'profesional' tier (the wizard's plan-picker step was removed).
        plan: 'profesional',
        botName: form.botName.trim(),
        welcomeMessage: form.welcome.trim(),
        owner: { name: form.ownerName.trim(), email: form.ownerEmail.trim() },
      }
      const { id } = await createTenant(payload)
      // Land ON the client, not back on the list: their Resumen hosts the
      // "Camino a en vivo" checklist — the natural next step of the call. If
      // the operator said "conectar ahora", start them on Canales directly.
      void navigate(
        form.connectMeta === 'now' ? `/admin/tenants/${id}?tab=identities` : `/admin/tenants/${id}`,
      )
    } catch (err) {
      // Surface the failure instead of navigating away as if it worked — a silent
      // create looked like "nothing happened".
      const message = apiErrorMessage(
        err,
        'No se pudo crear el cliente. Revisa los datos e inténtalo de nuevo.',
      )
      setSubmitError(message)
      toast.error('No se pudo crear el cliente', { description: message })
    } finally {
      setSubmitting(false)
    }
  }

  function onBack(): void {
    const prev = STEPS[index - 1]
    if (prev) setStepId(prev.id)
  }

  return (
    <div data-testid="admin-tenant-new" className="mx-auto w-full max-w-xl">
      <PageHeader
        breadcrumb={[{ label: 'Clientes', href: '/admin/tenants' }]}
        title="Nuevo cliente"
        subtitle="Da de alta un negocio y su primer agente."
      />

      {submitError ? (
        <div className="mt-4">
          <Banner tone="sens" role="alert">
            {submitError}
          </Banner>
        </div>
      ) : null}

      <div className="mt-5 rounded-2xl border border-line bg-surface p-6">
        <Wizard
          steps={STEPS}
          currentId={stepId}
          onCancel={() => void navigate('/admin/tenants')}
          onBack={onBack}
          onNext={() => void onNext()}
          nextDisabled={!stepValid[stepId] || submitting}
          {...(isLast ? { nextLabel: 'Crear cliente' } : {})}
        >
          {stepId === 'data' && (
            <>
              <StepHeading title="Datos del cliente" sub="Cómo se llama y a qué se dedica." />
              <div className="flex flex-col gap-4">
                <FormField label="Nombre del cliente" htmlFor="t-name">
                  <Input
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    placeholder="Ej. Clínica Santa Fe"
                  />
                </FormField>
                <WizardOptionCards
                  label="Vertical"
                  name="vertical"
                  value={form.vertical}
                  onChange={(v) => set('vertical', v as TenantVertical)}
                  options={[
                    { value: 'salud', title: 'Salud', description: 'Hospitales y clínicas' },
                    {
                      value: 'autos_nuevos',
                      title: 'Autos nuevos',
                      description: 'Agencias de autos nuevos',
                    },
                    {
                      value: 'autos_seminuevos',
                      title: 'Autos seminuevos',
                      description: 'Lotes y seminuevos',
                    },
                    {
                      value: 'empeno',
                      title: 'Casa de empeño',
                      description: 'Préstamos prendarios',
                    },
                    {
                      value: 'medico',
                      title: 'Médico',
                      description: 'Consultorio de un solo médico',
                    },
                  ]}
                />
              </div>
            </>
          )}

          {stepId === 'bot' && (
            <>
              <StepHeading
                title="Primer agente"
                sub="Lo configuramos a detalle después; aquí solo lo nombramos."
              />
              <div className="flex flex-col gap-4">
                <FormField label="Nombre del agente" htmlFor="t-bot">
                  <Input
                    value={form.botName}
                    onChange={(e) => set('botName', e.target.value)}
                    placeholder="Recepción"
                  />
                </FormField>
                <FormField label="Mensaje de bienvenida" htmlFor="t-welcome" helper="Opcional.">
                  <Textarea
                    value={form.welcome}
                    onChange={(e) => set('welcome', e.target.value)}
                    placeholder="¡Hola! Soy el agente de…"
                  />
                </FormField>
              </div>
            </>
          )}

          {stepId === 'meta' && (
            <>
              <StepHeading
                title="Conexión con Meta"
                sub="Vincula el número de WhatsApp ahora o más tarde."
              />
              <WizardOptionCards
                label="¿Cuándo conectamos WhatsApp?"
                name="connectMeta"
                value={form.connectMeta}
                onChange={(v) => set('connectMeta', v as 'now' | 'later')}
                options={[
                  {
                    value: 'now',
                    title: 'Conectar ahora',
                    description: 'Vincula el número de WhatsApp en seguida.',
                  },
                  {
                    value: 'later',
                    title: 'Conectar después',
                    description: 'Crea el cliente y vincula WhatsApp más tarde.',
                    recommended: true,
                  },
                ]}
              />
            </>
          )}

          {stepId === 'owner' && (
            <>
              <StepHeading
                title="Dueño de la cuenta"
                sub="Le enviaremos una invitación por correo."
              />
              <div className="flex flex-col gap-4">
                <FormField label="Nombre del Dueño" htmlFor="t-owner">
                  <Input
                    value={form.ownerName}
                    onChange={(e) => set('ownerName', e.target.value)}
                    placeholder="Ej. Lucía Morales"
                  />
                </FormField>
                <FormField label="Correo del Dueño" htmlFor="t-owner-email">
                  <Input
                    type="email"
                    value={form.ownerEmail}
                    onChange={(e) => set('ownerEmail', e.target.value)}
                    placeholder="lucia@clinicasantafe.mx"
                  />
                </FormField>
              </div>
            </>
          )}
        </Wizard>
      </div>
    </div>
  )
}
